import { useState, useEffect, useRef } from 'react';
import { NORWEGIAN_MUNICIPALITIES } from '@utils/norwegianMunicipalities';

// Strip accents and lowercase for fuzzy municipality name matching
const normalize = s =>
	s
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

/**
 * Reverse geocodes the center of a polygon to detect the Norwegian municipality.
 *
 * Features over the original inline implementation:
 * - Debounced: waits for the user to stop dragging before firing (avoids hammering Nominatim)
 * - Cancellable: aborts in-flight requests when polygon changes again
 * - User-Agent header: required by Nominatim usage policy
 *
 * @param {Array<{lat: number, lng: number}>} polygonPoints - Polygon vertices
 * @param {number} delay - Debounce delay in ms (default 800ms)
 * @returns {{ code: string, name: string } | null} Detected municipality or null
 */
export const useReverseGeocode = (polygonPoints, delay = 800) => {
	const [municipality, setMunicipality] = useState(null);
	const timerRef = useRef(null);
	const abortRef = useRef(null);

	useEffect(() => {
		// Clear any pending debounce timer and cancel any in-flight request
		clearTimeout(timerRef.current);
		abortRef.current?.abort();

		if (!polygonPoints || polygonPoints.length < 3) {
			setMunicipality(null);
			return;
		}

		timerRef.current = setTimeout(async () => {
			const controller = new AbortController();
			abortRef.current = controller;

			// Calculate centroid of the polygon
			const centerLat = polygonPoints.reduce((s, p) => s + p.lat, 0) / polygonPoints.length;
			const centerLng = polygonPoints.reduce((s, p) => s + p.lng, 0) / polygonPoints.length;

			try {
				const resp = await fetch(
					`https://nominatim.openstreetmap.org/reverse?lat=${centerLat}&lon=${centerLng}&format=json&zoom=10&addressdetails=1`,
					{
						headers: {
							'Accept-Language': 'no',
							'User-Agent': 'BioDronePortal/1.0 (https://biodroneportal.com)',
						},
						signal: controller.signal,
					}
				);

				const data = await resp.json();

				// Try address fields in priority order — same as original implementation
				const municipalityName =
					data?.address?.municipality ||
					data?.address?.city ||
					data?.address?.town ||
					data?.address?.county ||
					'';

				if (!municipalityName) {
					setMunicipality(null);
					return;
				}

				// Match against the Norwegian municipalities list (fuzzy: strip accents, lowercase)
				const match = NORWEGIAN_MUNICIPALITIES.find(
					m => normalize(m.name) === normalize(municipalityName)
				);

				setMunicipality({ code: match?.code || '', name: municipalityName });
			} catch (e) {
				if (e.name === 'AbortError') return; // Request was intentionally canceled — not an error
				console.warn('Reverse geocode failed:', e);
				setMunicipality(null);
			}
		}, delay);

		return () => {
			clearTimeout(timerRef.current);
			abortRef.current?.abort();
		};
	}, [polygonPoints, delay]);

	return municipality;
};
